import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Search, X, Loader } from 'lucide-react';

export interface PickerItem {
  id: number;
  label: string;
  sublabel?: string;
}

interface Props {
  selected: PickerItem[];
  onChange: (next: PickerItem[]) => void;
  /** פונקציית חיפוש בשרת — מקבלת מחרוזת ומחזירה פריטים. */
  searchFn: (query: string) => Promise<PickerItem[]>;
  placeholder?: string;
  disabled?: boolean;
  emptyText?: string;
  noResultsText?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.875rem;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #0073ea;
    box-shadow: 0 0 0 2px rgba(0, 115, 234, 0.15);
  }

  &:disabled {
    background-color: #f1f3f5;
    cursor: not-allowed;
  }
`;

const SearchIconWrap = styled.span`
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  color: #6c757d;
  pointer-events: none;
`;

const Dropdown = styled.ul`
  position: fixed;
  z-index: 2000;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  max-height: 280px;
  overflow-y: auto;
`;

const DropdownItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background: #f1f5fb;
  }

  &:first-child {
    background: #f1f5fb;
  }

  &:first-child:hover {
    background: #e3edfa;
  }
`;

const ItemSub = styled.span`
  font-size: 0.75rem;
  color: #6c757d;
`;

const DropdownEmpty = styled.li`
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  color: #6c757d;
  font-style: italic;
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.5rem 0.3rem 0.75rem;
  background: #e6f0ff;
  border: 1px solid #c2dbff;
  border-radius: 999px;
  font-size: 0.85rem;
  color: #0959b8;
`;

const TagSub = styled.span`
  font-size: 0.72rem;
  color: #5a7fb0;
`;

const RemoveTagButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: transparent;
  color: #0959b8;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover:not(:disabled) {
    background: rgba(9, 89, 184, 0.12);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spinner = styled(Loader)`
  animation: spin 0.9s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  padding: 0.25rem 0;
`;

const EntityAssignPicker: React.FC<Props> = ({
  selected,
  onChange,
  searchFn,
  placeholder = 'חפש להוספה...',
  disabled = false,
  emptyText = 'לא נבחרו פריטים',
  noResultsText = 'לא נמצאו תוצאות תואמות',
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<PickerItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!showDropdown) return;
    let cancelled = false;
    setSearching(true);
    searchFn(debouncedQuery)
      .then(list => {
        if (!cancelled) setResults(list);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, showDropdown, searchFn]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideWrapper && !insideDropdown) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!showDropdown) return;

    const updatePosition = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDropdown]);

  const selectedIds = new Set(selected.map(s => s.id));
  const visibleResults = results.filter(s => !selectedIds.has(s.id));

  const handlePick = (item: PickerItem) => {
    if (selectedIds.has(item.id)) return;
    onChange([...selected, item]);
    setQuery('');
    setShowDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (visibleResults.length > 0) {
        handlePick(visibleResults[0]);
      }
    }
  };

  const handleRemove = (id: number) => {
    onChange(selected.filter(s => s.id !== id));
  };

  return (
    <Container>
      <SearchWrapper ref={wrapperRef}>
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <SearchIconWrap>
          {searching ? <Spinner size={14} /> : <Search size={14} strokeWidth={2} />}
        </SearchIconWrap>
        {showDropdown && (
          <Dropdown
            ref={dropdownRef}
            style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
            onWheel={e => e.stopPropagation()}
          >
            {visibleResults.length === 0 && !searching ? (
              <DropdownEmpty>{noResultsText}</DropdownEmpty>
            ) : (
              visibleResults.map(item => (
                <DropdownItem
                  key={item.id}
                  onMouseDown={e => {
                    e.preventDefault();
                    handlePick(item);
                  }}
                >
                  <span>{item.label}</span>
                  {item.sublabel && <ItemSub>{item.sublabel}</ItemSub>}
                </DropdownItem>
              ))
            )}
          </Dropdown>
        )}
      </SearchWrapper>

      {selected.length === 0 ? (
        <EmptyState>{emptyText}</EmptyState>
      ) : (
        <TagsList>
          {selected.map(item => (
            <Tag key={item.id}>
              {item.label}
              {item.sublabel && <TagSub>({item.sublabel})</TagSub>}
              <RemoveTagButton
                type="button"
                onClick={() => handleRemove(item.id)}
                disabled={disabled}
                title="הסר"
              >
                <X size={12} strokeWidth={2.5} />
              </RemoveTagButton>
            </Tag>
          ))}
        </TagsList>
      )}
    </Container>
  );
};

export default EntityAssignPicker;
